import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import api from '../lib/api';
import { Search } from 'lucide-react';

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState(localStorage.getItem('betti_sort_by') || 'relevance');

  useEffect(() => {
    api.get('/categories').then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const q = searchParams.get('search');
    const url = q ? `/products?search=${encodeURIComponent(q)}` : '/products';
    api.get(url).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [searchParams]);

  const sortedProducts = useMemo(() => {
    const arr = [...products];
    if (sortBy === 'alphabetical') arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'it'));
    else if (sortBy === 'alphabetical-desc') arr.sort((a, b) => (b.name || '').localeCompare(a.name || '', 'it'));
    else if (sortBy === 'price-asc') arr.sort((a, b) => (a.price_per_day || 0) - (b.price_per_day || 0));
    else if (sortBy === 'price-desc') arr.sort((a, b) => (b.price_per_day || 0) - (a.price_per_day || 0));
    // relevance = keep server order (created_at desc / featured logic)
    return arr;
  }, [products, sortBy]);

  const changeSort = (v) => {
    setSortBy(v);
    localStorage.setItem('betti_sort_by', v);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) setSearchParams({ search: search.trim() });
    else setSearchParams({});
  };

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-6 py-8 fade-in">
      <h1 className="text-4xl font-black brand-text text-neutral-900">Noleggio</h1>
      <p className="text-neutral-600 mt-2">Esplora il nostro catalogo di attrezzature audio professionali.</p>

      <form onSubmit={handleSearch} className="mt-6 flex gap-2 max-w-2xl">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per nome, marca, descrizione..."
            className="w-full pl-10 pr-4 py-2.5 border border-neutral-300 rounded focus:outline-none focus:ring-2 focus:ring-[#d62828]/30 focus:border-[#d62828]"
          />
        </div>
        <button type="submit" className="px-5 py-2.5 bg-[#d62828] text-white rounded font-semibold hover:bg-[#b41f1f] transition-colors">
          Cerca
        </button>
      </form>

      <div className="mt-8 grid lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="space-y-1">
          <h3 className="font-bold text-neutral-800 mb-3">Categorie</h3>
          <Link to="/noleggio" className="block px-3 py-2 rounded text-sm font-medium hover:bg-neutral-100">Tutti i prodotti</Link>
          {categories.map((c) => (
            <Link key={c.id} to={`/categoria/${c.slug}`} className="block px-3 py-2 rounded text-sm font-medium text-neutral-700 hover:bg-neutral-100 hover:text-[#d62828]">
              {c.name}
            </Link>
          ))}
        </aside>

        {/* Product grid */}
        <div>
          {loading ? (
            <div className="text-neutral-500">Caricamento...</div>
          ) : products.length === 0 ? (
            <div className="text-center py-16 text-neutral-500">Nessun prodotto trovato.</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <p className="text-sm text-neutral-500">{products.length} prodotti</p>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-neutral-600 font-medium">Ordina per:</label>
                  <select
                    value={sortBy}
                    onChange={(e) => changeSort(e.target.value)}
                    className="px-3 py-1.5 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#d62828]/30"
                    data-testid="sort-select"
                  >
                    <option value="relevance">Rilevanza (default)</option>
                    <option value="alphabetical">Nome A → Z</option>
                    <option value="alphabetical-desc">Nome Z → A</option>
                    <option value="price-asc">Prezzo crescente</option>
                    <option value="price-desc">Prezzo decrescente</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {sortedProducts.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Catalog;
